import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Paperclip, Upload, X, Download, FileText, FileImage, FileArchive, File as FileIcon, Loader2 } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import ConfirmDialog from '../ConfirmDialog';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const IconForFile = ({ nombre, className = "" }) => {
  const ext = (nombre || '').toLowerCase().split('.').pop();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    return <FileImage className={className} strokeWidth={1.5} />;
  }
  if (['pdf', 'doc', 'docx', 'txt', 'md'].includes(ext)) {
    return <FileText className={className} strokeWidth={1.5} />;
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return <FileArchive className={className} strokeWidth={1.5} />;
  }
  return <FileIcon className={className} strokeWidth={1.5} />;
};

const Attachments = ({ tareaId, adjuntos = [], onChanged }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState([]); // [{tempId, nombre, progress}]
  const [adjToDelete, setAdjToDelete] = useState(null);
  const inputRef = useRef(null);

  const uploadFiles = useCallback(async (files) => {
    if (!tareaId) return;
    const list = Array.from(files);

    const tempUploads = list.map((f) => ({
      tempId: Math.random().toString(36).slice(2),
      nombre: f.name,
    }));
    setUploading((prev) => [...prev, ...tempUploads]);

    try {
      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        const fd = new FormData();
        fd.append('archivo', file);
        try {
          await axiosInstance.post(`/api/tareas/${tareaId}/adjuntos`, fd);
        } catch (err) {
          console.error('Error subiendo adjunto:', err);
        }
      }
      onChanged?.();
    } finally {
      setUploading((prev) =>
        prev.filter((u) => !tempUploads.some((t) => t.tempId === u.tempId))
      );
    }
  }, [tareaId, onChanged]);

  const handleDownload = async (adj) => {
    try {
      const resp = await axiosInstance.get(
        `/api/tareas/${tareaId}/adjuntos/${adj.id}/descargar`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = adj.nombre_archivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando:', err);
    }
  };

  const doDelete = async () => {
    if (!adjToDelete) return;
    const adj = adjToDelete;
    setAdjToDelete(null);
    try {
      await axiosInstance.delete(`/api/tareas/${tareaId}/adjuntos/${adj.id}`);
      onChanged?.();
    } catch (err) {
      console.error('Error eliminando adjunto:', err);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  const total = adjuntos.length + uploading.length;

  return (
    <section>
      <div className="flex items-center gap-1.5 mb-2">
        <Paperclip size={11} strokeWidth={2} className="text-slate-400" />
        <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
          Adjuntos
        </span>
        {total > 0 && (
          <span className="text-[10px] text-slate-400 tabular-nums">{total}</span>
        )}
        <div className="flex-1 h-px bg-slate-200/60" />
      </div>

      {/* Lista */}
      <ul className="space-y-1 mb-2">
        <AnimatePresence initial={false}>
          {adjuntos.map((adj) => (
            <motion.li
              key={adj.id}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.15 }}
              className="group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 transition-colors"
            >
              <IconForFile nombre={adj.nombre_archivo} className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <button
                onClick={() => handleDownload(adj)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="text-xs text-slate-800 truncate hover:text-cyan-600 transition-colors">
                  {adj.nombre_archivo}
                </div>
                <div className="text-[10px] text-slate-400 tabular-nums">
                  {formatSize(adj.tamano_bytes)}
                  {adj.subido_por?.nombre ? ` · ${adj.subido_por.nombre}` : ''}
                </div>
              </button>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleDownload(adj)}
                  className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                  title="Descargar"
                >
                  <Download size={12} />
                </button>
                <button
                  onClick={() => setAdjToDelete(adj)}
                  className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                  title="Eliminar"
                >
                  <X size={12} />
                </button>
              </div>
            </motion.li>
          ))}
        </AnimatePresence>
        {uploading.map((u) => (
          <li
            key={u.tempId}
            className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-cyan-50/50"
          >
            <Loader2 size={14} className="text-cyan-500 animate-spin flex-shrink-0" />
            <span className="text-xs text-slate-600 truncate flex-1">{u.nombre}</span>
            <span className="text-[10px] text-cyan-600 font-medium">Subiendo...</span>
          </li>
        ))}
      </ul>

      {/* Dropzone */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs rounded-md border border-dashed transition-all ${
          dragOver
            ? 'border-cyan-400 bg-cyan-50 text-cyan-700'
            : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-600'
        }`}
      >
        <Upload size={12} strokeWidth={2} />
        <span>{dragOver ? 'Suelta para subir' : 'Subir archivo o arrastrar aquí'}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) uploadFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <ConfirmDialog
        isOpen={!!adjToDelete}
        title="¿Eliminar este adjunto?"
        message={adjToDelete ? `Se quitará "${adjToDelete.nombre_archivo}" de la tarea. La acción no se puede deshacer.` : ''}
        variant="danger"
        confirmLabel="Eliminar"
        onConfirm={doDelete}
        onClose={() => setAdjToDelete(null)}
      />
    </section>
  );
};

export default Attachments;
